/**
 * Table rules. Every value here is host-editable between rounds, so the engine
 * reads them as data instead of hardcoding a house style.
 */
import type { SideBetId } from './types.js';

export interface TableRules {
  /** Decks in the shoe. */
  decks: number;
  /** Rebuild the shoe once this fraction or less remains. */
  penetration: number;
  /** H17 when true, S17 when false. */
  dealerHitsSoft17: boolean;
  /** 1.5 for 3:2, 1.2 for 6:5. */
  blackjackPayout: number;
  minBet: number;
  maxBet: number;
  /** Extra hands a seat may split into. 0 disables splitting; 3 allows four hands. */
  maxSplits: number;
  /** Split aces normally draw exactly one card each and cannot be hit. */
  hitSplitAces: boolean;
  resplitAces: boolean;
  doubleAfterSplit: boolean;
  /** Late surrender: offered on the first two cards, after the dealer peek. */
  surrenderAllowed: boolean;
  insuranceAllowed: boolean;
  /** How many seats one player may hold at once. */
  seatsPerPlayer: number;
  /**
   * The table's single buy-in, in chips. One bankroll follows a player across
   * a blackjack/Hold'em swap, so this is deliberately not per-game.
   */
  startingChips: number;
  /** Lets a busted-out player top back up to `startingChips`. */
  rebuyAllowed: boolean;
  /** Auto-sits-out anyone who has not bet when this expires. 0 disables. */
  betTimeoutMs: number;
  /** Auto-stands the acting hand when this expires. 0 disables. */
  actionTimeoutMs: number;
  /** Insurance decision window. 0 disables. */
  insuranceTimeoutMs: number;
  sideBets: SideBetId[];
  /** Cap on any single side-bet stake. */
  sideBetMax: number;
}

export const DEFAULT_RULES: TableRules = {
  decks: 6,
  penetration: 0.25,
  dealerHitsSoft17: false,
  blackjackPayout: 1.5,
  minBet: 5,
  maxBet: 500,
  maxSplits: 3,
  hitSplitAces: false,
  resplitAces: false,
  doubleAfterSplit: true,
  surrenderAllowed: true,
  insuranceAllowed: true,
  seatsPerPlayer: 1,
  startingChips: 1000,
  rebuyAllowed: true,
  betTimeoutMs: 45_000,
  actionTimeoutMs: 30_000,
  insuranceTimeoutMs: 15_000,
  sideBets: [],
  sideBetMax: 100,
};

/** Bounds every numeric rule, so a hostile client cannot deal a 900-deck shoe. */
export const RULE_LIMITS = {
  decks: { min: 1, max: 8 },
  penetration: { min: 0.1, max: 0.9 },
  blackjackPayout: { min: 1, max: 3 },
  minBet: { min: 1, max: 1_000 },
  maxBet: { min: 1, max: 100_000 },
  maxSplits: { min: 0, max: 3 },
  seatsPerPlayer: { min: 1, max: 7 },
  startingChips: { min: 100, max: 1_000_000 },
  betTimeoutMs: { min: 0, max: 600_000 },
  actionTimeoutMs: { min: 0, max: 600_000 },
  insuranceTimeoutMs: { min: 0, max: 600_000 },
  sideBetMax: { min: 1, max: 100_000 },
} as const;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Parse an untrusted number. `Number()` alone is too generous — it turns null,
 * '' and true into real numbers — so only genuine numbers and numeric strings
 * are accepted; everything else falls back.
 */
const num = (raw: unknown, fallback: number): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const int = (raw: unknown, fallback: number, key: keyof typeof RULE_LIMITS): number => {
  const { min, max } = RULE_LIMITS[key];
  return clamp(Math.round(num(raw, fallback)), min, max);
};

/** Same parsing, but keeps the fractional part. */
const frac = (raw: unknown, fallback: number, key: keyof typeof RULE_LIMITS): number => {
  const { min, max } = RULE_LIMITS[key];
  return clamp(num(raw, fallback), min, max);
};

const bool = (raw: unknown, fallback: boolean): boolean =>
  typeof raw === 'boolean' ? raw : fallback;

export const VALID_SIDE_BETS: SideBetId[] = [
  'perfect_pairs',
  'twenty_one_plus_three',
  'lucky_lucky',
  'suit_em_up',
  'royal_match',
  'over_13',
  'under_13',
];

/**
 * Fold an untrusted patch into the current rules. Unknown keys are dropped and
 * every number is clamped, so the result is always a legal table.
 */
export function normalizeRules(
  patch: Partial<Record<keyof TableRules, unknown>>,
  current: TableRules = DEFAULT_RULES,
): TableRules {
  const has = (key: keyof TableRules) => Object.hasOwn(patch, key);

  const decks = has('decks') ? int(patch.decks, current.decks, 'decks') : current.decks;

  const penetration = has('penetration')
    ? frac(patch.penetration, current.penetration, 'penetration')
    : current.penetration;

  const blackjackPayout = has('blackjackPayout')
    ? frac(patch.blackjackPayout, current.blackjackPayout, 'blackjackPayout')
    : current.blackjackPayout;

  const minBet = has('minBet') ? int(patch.minBet, current.minBet, 'minBet') : current.minBet;
  // The ceiling can never fall below the floor, whichever of the two moved.
  const maxBet = Math.max(
    minBet,
    has('maxBet') ? int(patch.maxBet, current.maxBet, 'maxBet') : current.maxBet,
  );

  const sideBets = has('sideBets')
    ? [...new Set((Array.isArray(patch.sideBets) ? patch.sideBets : []).filter(
        (id): id is SideBetId => VALID_SIDE_BETS.includes(id as SideBetId),
      ))]
    : current.sideBets;

  return {
    decks,
    penetration,
    dealerHitsSoft17: has('dealerHitsSoft17')
      ? bool(patch.dealerHitsSoft17, current.dealerHitsSoft17)
      : current.dealerHitsSoft17,
    blackjackPayout,
    minBet,
    maxBet,
    maxSplits: has('maxSplits') ? int(patch.maxSplits, current.maxSplits, 'maxSplits') : current.maxSplits,
    hitSplitAces: has('hitSplitAces') ? bool(patch.hitSplitAces, current.hitSplitAces) : current.hitSplitAces,
    resplitAces: has('resplitAces') ? bool(patch.resplitAces, current.resplitAces) : current.resplitAces,
    doubleAfterSplit: has('doubleAfterSplit')
      ? bool(patch.doubleAfterSplit, current.doubleAfterSplit)
      : current.doubleAfterSplit,
    surrenderAllowed: has('surrenderAllowed')
      ? bool(patch.surrenderAllowed, current.surrenderAllowed)
      : current.surrenderAllowed,
    insuranceAllowed: has('insuranceAllowed')
      ? bool(patch.insuranceAllowed, current.insuranceAllowed)
      : current.insuranceAllowed,
    seatsPerPlayer: has('seatsPerPlayer')
      ? int(patch.seatsPerPlayer, current.seatsPerPlayer, 'seatsPerPlayer')
      : current.seatsPerPlayer,
    startingChips: has('startingChips')
      ? int(patch.startingChips, current.startingChips, 'startingChips')
      : current.startingChips,
    rebuyAllowed: has('rebuyAllowed') ? bool(patch.rebuyAllowed, current.rebuyAllowed) : current.rebuyAllowed,
    betTimeoutMs: has('betTimeoutMs')
      ? int(patch.betTimeoutMs, current.betTimeoutMs, 'betTimeoutMs')
      : current.betTimeoutMs,
    actionTimeoutMs: has('actionTimeoutMs')
      ? int(patch.actionTimeoutMs, current.actionTimeoutMs, 'actionTimeoutMs')
      : current.actionTimeoutMs,
    insuranceTimeoutMs: has('insuranceTimeoutMs')
      ? int(patch.insuranceTimeoutMs, current.insuranceTimeoutMs, 'insuranceTimeoutMs')
      : current.insuranceTimeoutMs,
    sideBets,
    sideBetMax: has('sideBetMax') ? int(patch.sideBetMax, current.sideBetMax, 'sideBetMax') : current.sideBetMax,
  };
}
